import { Button, Img, Heading, Text } from "./..";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ImagesCarousel from "components/ImagesCarousel";
import { formatPrice } from "utils/formatPrice";
import AuctionIndicator from "components/AuctionIndicator";

const DEFAULT_IMG = { image_url: "/images/img_car11_qgcqjcnfp_428x738.png", __isDefault: true };
const hasDocUrl = (value) => String(value ?? "").trim() !== "";
const AUCTION_COUNTDOWN_WINDOW_MS = 24 * 60 * 60 * 1000;
const AUCTION_DURATION_MS = 5 * 60 * 1000;

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatAuctionDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  if (!Number.isFinite(date.getTime())) return "-";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatCountdown = (targetMs, nowMs) => {
  const diffSeconds = Math.max(0, Math.floor((targetMs - nowMs) / 1000));
  const days = Math.floor(diffSeconds / 86400);
  const hours = Math.floor((diffSeconds % 86400) / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = diffSeconds % 60;
  const clock = [hours, minutes, seconds].map((part) => String(part).padStart(2, "0")).join(":");

  return days > 0 ? `${days} ימים ${clock}` : clock;
};

function DocBadgeIcon({ type }) {
  const isPriceList = type === "priceList";
  const accent = isPriceList ? "#111827" : "#CE0000";
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M7 3h7l4 4v14H7V3Z"
        fill="#fff"
        stroke={accent}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M14 3v5h5" stroke={accent} strokeWidth="1.8" strokeLinejoin="round" />
      {isPriceList ? (
        <path d="M10 13h4.5M10 16h3M16 12.5v4" stroke={accent} strokeWidth="1.6" strokeLinecap="round" />
      ) : (
        <path d="M9.5 12h5M9.5 16h7" stroke={accent} strokeWidth="1.6" strokeLinecap="round" />
      )}
    </svg>
  );
}

const statusMap = {
//  0: { txt: "ממתין להצעה", clr: "#808080cc" },
    0: { txt: "ממתין להצעה", clr: "#CE0000"  },
  1: { txt: "ממתין לאישור", clr: "#CE0000" },
  2: { txt: "מאושר", clr: "#619D1C" },
  3: { txt: "הצעה שנדחתה", clr: "#666666" },
  4: { txt: "ארכיון", clr: "#666666" },
  5: { txt: "לא מעונין", clr: "#666666" }, // NEW
};


export default function CarCardTemplate({
  id,
  year,
  manufacturer,
  model,
  hand,
  gearbox,
  engine_type,
  images = [],
  offer_status,
  offered_price,
  approved_other,
  license_doc_url,
  price_list_doc_url,
  parts_list_doc_url,
  ...props
}) {
  const imagesSafe = useMemo(() => {
    const has = Array.isArray(images) && images.length > 0;
    return has ? images : [DEFAULT_IMG];
  }, [images]);

  const formatOfferText = (text) => {
    if (text === null || text === undefined || text === "") return "";
    const s = String(text).trim();
    if (s.includes("-")) {
      return s
        .split("-")
        .map((v) => formatPrice(String(v).trim()))
        .join(" - ");
    }
    return formatPrice(s);
  };

  const firstPositivePrice = (...values) => {
    for (const value of values) {
      const number = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
      if (Number.isFinite(number) && number > 0) return value;
    }
    return values.find((value) => value !== null && value !== undefined && value !== "") ?? "";
  };

  const displayStatus =
    Number(offer_status) === 0 && Number(approved_other) === 1 ? 4 : Number(offer_status);

  const offerText = props.offer_range ?? offered_price ?? "";
  const offerPriceLabel = props.offer_price_label ?? props.offerPriceLabel ?? "מחיר ההצעה";
  const hasOfferText = offerText !== null && offerText !== undefined && String(offerText).trim() !== "";
  const isAuction = Number(props.is_auction ?? props.isAuction ?? 0) === 1 || props.is_auction === true;
  const [nowMs, setNowMs] = useState(Date.now());
  const parseTime = (value) => {
    if (!value) return 0;
    const parsed = new Date(String(value).replace(" ", "T")).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const auctionStartMs = parseTime(
    props.auction_start_datetime_iso ??
      props.auction_start_at_iso ??
      props.auction_start_datetime ??
      props.auction_start_at
  );
  const auctionEndMs = parseTime(
    props.auction_active_until_iso ??
      props.auction_end_time_iso ??
      props.auction_active_until ??
      props.auction_end_time ??
      props.auction_end_at
  );
  const serverNowValue = props.server_now_iso ?? props.server_now ?? props.server_time;
  const serverNowMs = parseTime(serverNowValue);
  const serverOffsetMs = useMemo(() => (serverNowMs ? Date.now() - serverNowMs : 0), [serverNowMs]);
  const remainingSecondsFromServer = toFiniteNumber(props.remaining_seconds ?? props.seconds_until_end);
  const secondsUntilStartFromServer = toFiniteNumber(props.seconds_until_start);
  const responseStartMs =
    serverNowMs && secondsUntilStartFromServer !== null
      ? serverNowMs + secondsUntilStartFromServer * 1000
      : 0;
  const responseEndMs =
    serverNowMs && remainingSecondsFromServer !== null
      ? serverNowMs + remainingSecondsFromServer * 1000
      : 0;
  const effectiveAuctionStartMs = responseStartMs || auctionStartMs;
  const effectiveAuctionEndMs = responseEndMs || auctionEndMs || (effectiveAuctionStartMs ? effectiveAuctionStartMs + AUCTION_DURATION_MS : 0);
  const effectiveNowMs = serverNowMs ? nowMs - serverOffsetMs : nowMs;
  const auctionStatus = String(props.auction_status ?? props.status_auction ?? "").toLowerCase();
  const auctionHasStartedFlag = props.has_started === true || Number(props.auction_has_started ?? props.auctionHasStarted ?? 0) === 1;
  const auctionHasNotStartedFlag =
    props.has_started === false ||
    Number(props.auction_has_started ?? props.auctionHasStarted ?? Number.NaN) === 0;
  const auctionIsActiveFlag = Number(props.auction_is_active ?? props.auctionIsActive ?? 0) === 1 || ["active", "live"].includes(auctionStatus);
  const isEndedAuction =
    isAuction &&
    (Number(props.auction_has_ended ?? props.auctionHasEnded ?? 0) === 1 ||
      props.has_ended === true ||
      Boolean(props.auction_finalized_at) ||
      ["ended", "finalized"].includes(auctionStatus));
  const isOpenAuction =
    isAuction &&
    !isEndedAuction &&
    (Number(props.auction_is_open ?? props.auctionIsOpen ?? 0) === 1 ||
      auctionIsActiveFlag ||
      auctionHasStartedFlag ||
      auctionHasNotStartedFlag ||
      (effectiveAuctionStartMs > 0 && (effectiveAuctionStartMs > effectiveNowMs || (effectiveAuctionEndMs > 0 && effectiveAuctionEndMs > effectiveNowMs))) ||
      (!auctionStartMs && !auctionEndMs));
  const isFutureAuction =
    isOpenAuction &&
    !auctionIsActiveFlag &&
    !auctionHasStartedFlag &&
    (effectiveAuctionStartMs > effectiveNowMs || auctionHasNotStartedFlag || ["scheduled", "future"].includes(auctionStatus));
  const isActiveAuction =
    isOpenAuction &&
    !isFutureAuction &&
    (auctionIsActiveFlag ||
      auctionHasStartedFlag ||
      (effectiveAuctionStartMs > 0 && effectiveAuctionStartMs <= effectiveNowMs && (!effectiveAuctionEndMs || effectiveAuctionEndMs > effectiveNowMs)));
  const futureAuctionCountdownTargetMs =
    isFutureAuction &&
    effectiveAuctionStartMs > 0 &&
    effectiveAuctionStartMs > effectiveNowMs &&
    effectiveAuctionStartMs - effectiveNowMs <= AUCTION_COUNTDOWN_WINDOW_MS
      ? effectiveAuctionStartMs
      : 0;
  const activeAuctionCountdownTargetMs = isActiveAuction && effectiveAuctionEndMs > effectiveNowMs ? effectiveAuctionEndMs : 0;
  const auctionCountdownTargetMs = activeAuctionCountdownTargetMs || futureAuctionCountdownTargetMs;
  const shouldShowAuctionCountdown = auctionCountdownTargetMs > 0;
  const auctionStartValue =
    props.auction_start_datetime_iso ??
    props.auction_start_at_iso ??
    props.auction_start_datetime ??
    props.auction_start_at;
  const auctionFooterText = shouldShowAuctionCountdown
    ? isActiveAuction
      ? `מכרז פעיל: ${formatCountdown(auctionCountdownTargetMs, effectiveNowMs)}`
      : `מתחיל בעוד: ${formatCountdown(auctionCountdownTargetMs, effectiveNowMs)}`
    : isFutureAuction
    ? `מתחיל ב: ${formatAuctionDateTime(auctionStartValue)}`
    : "";
  const auctionPrice = firstPositivePrice(
    props.highest_offer,
    props.highest_bid,
    props.max_offer,
    props.minimum_bid,
    props.recommended_price,
    props.asking_price
  );
  const statusInfo = statusMap[displayStatus] ?? statusMap[0];
  const txt = isFutureAuction ? "מכרז עתידי" : isActiveAuction ? "מכרז פעיל" : statusInfo.txt;
  const clr = isOpenAuction ? "rgb(255, 140, 66)" : statusInfo.clr;
  const docBadges = [
    { key: "license", label: "מידע משרד התחבורה", type: "license", show: hasDocUrl(license_doc_url ?? props.license_doc_url) },
    { key: "priceList", label: "מחיר לוי יצחק", type: "priceList", show: hasDocUrl(price_list_doc_url ?? props.price_list_doc_url) },
    { key: "partsList", label: "חלקי חילוף", type: "partsList", show: hasDocUrl(parts_list_doc_url ?? props.parts_list_doc_url) },
  ].filter((badge) => badge.show);

  useEffect(() => {
    if (!isOpenAuction) return undefined;

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isOpenAuction]);

  const navigate = useNavigate();
  const navigateCarProfil = () => navigate(`${id}`);

  return (
    <div {...props} className={`${props.className} relative flex flex-col w-full rounded-[16px] overflow-hidden border border-solid border-gray-200 bg-secondary-primary_white`}>
      {/* Container לתמונה עם גובה קבוע */}
      <div className="relative w-full h-[220px] sm:h-[250px] overflow-hidden">
        <AuctionIndicator item={{ ...props, is_auction: isAuction }} />

<Button
  shape="round"
  className="ml-[15px] mt-[17px] rounded-[14px] font-medium capitalize text-white font-[DM Sans]"
  style={{
    zIndex: 999,
    backgroundColor: clr,
    color: "white",
    padding: "8px 20px",
    position: "absolute",
    top: 0,
    left: 0,
  }}
>
  {txt}
</Button>

        {(isOpenAuction || hasOfferText) && (
          <div className="absolute w-full bottom-0 flex justify-center py-1" style={{ backgroundColor: clr, opacity: 0.8, zIndex: 999 }}>
            <Heading size="headingxl" className="!font-heebo text-[14px] font-bold !text-secondary-primary_white">
              {isOpenAuction
                ? `החל מ־ ₪${formatOfferText(auctionPrice)}`
                : String(offerText).includes("-")
                ? `טווח הצעות: ₪${formatOfferText(offerText).replace(" - ", " - ₪")}`
                : `${offerPriceLabel}: ₪${formatOfferText(offerText)}`}
            </Heading>
          </div>
        )}

        {/* עטיפה לקרוסלה כדי לוודא גובה אחיד */}
        <div className="w-full h-full">
            <ImagesCarousel images={imagesSafe} className="h-full w-full object-cover" />
        </div>
      </div>

      {/* חלק המידע - גובה קבוע או גמיש אך אחיד */}
      <div className="flex flex-col gap-6 p-5 flex-grow">
        <div className="flex flex-col items-center gap-3">
          <div className="flex flex-col items-center gap-1">
            <Heading as="h6" className="!font-heebo text-[18px] font-bold !text-black-900 text-center">
              {model}
            </Heading>
            <div className="flex items-center justify-center gap-2">
              <Text as="p" className="text-[14px] font-normal">{year}</Text>
              <div className="h-[5px] w-[5px] rounded-full bg-red-600" />
              <Text as="p" className="text-[14px] font-normal">{manufacturer}</Text>
            </div>
      
          </div>

<div className="grid grid-cols-3 w-full  pt-4">
  <div className="flex flex-col items-center gap-1 px-1 min-w-0">
    <Img src="/images/img_icon_black_900_01.svg" alt="מנוע" className="h-[18px] shrink-0" />
    <Text as="p" className="text-[13px] font-normal text-center truncate w-full">
      {engine_type}
    </Text>
  </div>

  <div className="flex flex-col items-center gap-1 px-1 min-w-0 border-x border-gray-100">
    <Img src="/images/img_artboard_2_copy_3_black_900_03_3.svg" alt="יד" className="h-[18px] shrink-0" />
    <Text as="p" className="text-[13px] font-normal text-center truncate w-full">
      יד {hand}
    </Text>
  </div>

  <div className="flex flex-col items-center gap-1 px-1 min-w-0">
    <Img src="/images/img_icon_black_900.svg" alt="גיר" className="h-[18px] shrink-0" />
    <Text as="p" className="text-[13px] font-normal text-center truncate w-full">
      {gearbox}
    </Text>
  </div>
</div>
      <div className="flex h-6 items-center justify-center gap-1.5">
              {docBadges.map((badge) => (
                <span
                  key={badge.key}
                  title={badge.label}
                  aria-label={badge.label}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white"
                >
                  <DocBadgeIcon type={badge.type} />
                </span>
              ))}
            </div>
   
        </div>   {auctionFooterText && (
        <Text
          as="p"
          className={`text-right ${
            isActiveAuction ? "text-[16px] font-bold" : "text-[14px] font-normal !text-gray-600"
          }`}
          style={isActiveAuction ? { color: clr } : undefined}
        >
          {auctionFooterText}
        </Text>
      )}
      </div>
    </div>
  );
}
